import React, { useState, useEffect } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import axios from 'axios';
import { Clock, User } from 'lucide-react';
import { ResponsiveImage } from '../components/ResponsiveImage';

const API_URL = process.env.REACT_APP_BACKEND_URL || 'https://motoring-daily.onrender.com';

export const SearchPage = () => {
  const [searchParams] = useSearchParams();
  const query = searchParams.get('q') || '';
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let isMounted = true;

    const searchArticles = async () => {
      try {
        const { data } = await axios.get(`${API_URL}/api/articles/search?q=${encodeURIComponent(query)}`);
        if (isMounted) setResults(data);
      } catch (error) {
        console.error('Error searching articles:', error);
      } finally {
        if (isMounted) setLoading(false);
      }
    };

    if (query) {
      searchArticles();
    } else {
      setResults([]);
      setLoading(false);
    }

    return () => {
      isMounted = false;
    };
  }, [query]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-xl font-heading">Searching...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F8F9FA] py-16">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <h1 className="text-4xl sm:text-5xl lg:text-6xl tracking-tighter font-black mb-4 font-heading">
          Search Results
        </h1>
        <p className="text-lg text-[#666666] mb-12">
          Found {results.length} results for "{query}"
        </p>

        {results.length === 0 ? (
          <div className="text-center py-12">
            <p className="text-xl text-[#666666] mb-6">No results found. Try different keywords.</p>
            <Link
              to="/"
              className="bg-[#00A859] hover:bg-[#008a49] text-white font-bold uppercase tracking-wider py-3 px-6 rounded-none transition-colors duration-200 inline-block"
            >
              Go Home
            </Link>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8">
            {results.map((article) => (
              <Link
                key={article.id}
                to={`/article/${article.id}`}
                className="bg-white border border-[#E5E7EB] rounded-sm overflow-hidden group hover:-translate-y-1 hover:shadow-xl transition-all duration-300"
                data-testid="search-result-card"
              >
                <div className="relative h-48 overflow-hidden">
                  <ResponsiveImage
                    src={article.image_url}
                    alt={article.title}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                    width={800}
                    quality={70}
                  />
                  <span className="absolute top-4 left-4 bg-[#00A859] text-white text-xs font-bold uppercase tracking-wider py-1 px-3">
                    {article.category}
                  </span>
                </div>
                <div className="p-6">
                  <h3 className="text-xl font-bold tracking-tight mb-2 font-heading">
                    {article.title}
                  </h3>
                  <p className="text-base leading-relaxed text-[#666666] mb-4">
                    {article.excerpt}
                  </p>
                  <div className="flex items-center space-x-4 text-sm text-[#666666]">
                    <span className="flex items-center">
                      <User className="w-4 h-4 mr-1" />
                      {article.author}
                    </span>
                    <span className="flex items-center">
                      <Clock className="w-4 h-4 mr-1" />
                      {article.read_time} min
                    </span>
                  </div>
                </div>
              </Link>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};